import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  OnChangeFn,
  PaginationState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "components/ui/button";
import { Skeleton } from "components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  rowCount: number;
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  // Makes each row a link, so it can be opened in a new tab.
  getRowHref?: (row: TData) => string;
  loading?: boolean;
  noResultsMessage?: React.ReactNode;
  // A full-width row under the table body.
  footer?: React.ReactNode;
}

const MAX_SKELETON_ROWS = 10;
const SKELETON_WIDTHS = ["w-24", "w-40", "w-16", "w-20"];

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
}: {
  column: Column<TData, TValue>;
  title: string;
}) {
  if (!column.getCanSort()) {
    return <>{title}</>;
  }
  const sorted = column.getIsSorted();
  const Icon =
    sorted === "asc" ? ArrowUp : sorted === "desc" ? ArrowDown : ArrowUpDown;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-2 h-7 gap-1 px-2 text-xs font-medium text-muted-foreground hover:bg-transparent hover:text-foreground"
      onClick={column.getToggleSortingHandler()}
    >
      {title}
      <Icon className={sorted ? "text-foreground" : "opacity-60"} />
    </Button>
  );
}

export function DataTable<TData, TValue>({
  columns,
  data,
  rowCount,
  pagination,
  onPaginationChange,
  sorting = [],
  onSortingChange,
  getRowHref,
  loading = false,
  noResultsMessage = "No results.",
  footer,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    rowCount,
    state: { pagination, sorting },
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableSorting: !!onSortingChange,
    getCoreRowModel: getCoreRowModel(),
  });
  const { rows } = table.getRowModel();
  // Rows the current page should have, when the total is already known.
  const expectedRows = rowCount - pagination.pageIndex * pagination.pageSize;
  const skeletonRows = Math.max(
    1,
    Math.min(
      pagination.pageSize,
      MAX_SKELETON_ROWS,
      expectedRows > 0 ? expectedRows : MAX_SKELETON_ROWS,
    ),
  );

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-xl border bg-background shadow-sm">
        <Table>
          <TableHeader className="bg-muted">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    className="h-11 px-4 text-xs font-medium text-muted-foreground"
                    key={header.id}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody
            aria-busy={loading}
            className={
              loading && rows.length ? "opacity-60 transition-opacity" : ""
            }
          >
            {loading && !rows.length ? (
              Array.from({ length: skeletonRows }, (_, rowIndex) => (
                <TableRow key={rowIndex} className="hover:bg-transparent">
                  {columns.map((_, columnIndex) => (
                    <TableCell key={columnIndex} className="px-4 py-3">
                      <Skeleton
                        className={`h-4 ${
                          SKELETON_WIDTHS[columnIndex % SKELETON_WIDTHS.length]
                        }`}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length ? (
              rows.map((row) => {
                const href = getRowHref?.(row.original);
                return (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell, cellIndex) => {
                      const content = flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      );
                      return href ? (
                        <TableCell key={cell.id} className="p-0">
                          {/* Every cell links to the row so it can be clicked or
                              right-clicked anywhere; only the first is in the tab
                              order. The rest stay readable by screen readers. */}
                          <Link
                            to={href}
                            tabIndex={cellIndex === 0 ? undefined : -1}
                            className="block px-4 py-3 text-inherit hover:text-inherit focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring"
                          >
                            {content}
                          </Link>
                        </TableCell>
                      ) : (
                        <TableCell key={cell.id} className="px-4 py-3">
                          {content}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {noResultsMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {footer && (
            <TableFooter className="bg-muted/50 font-normal">
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={columns.length}
                  className="px-4 py-3 text-sm text-muted-foreground"
                >
                  {footer}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
      <div className="flex items-center justify-end gap-2">
        <span className="text-sm text-muted-foreground">
          Page {pagination.pageIndex + 1} of {Math.max(table.getPageCount(), 1)}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
